/**
 * Esquemas de autenticación.
 *
 * La política de contraseña es deliberadamente «larga antes que rara»: NIST 800-63B retiró la
 * exigencia de mayúsculas/símbolos porque produce `Password1!` en todas partes. Lo que se
 * exige es longitud y no estar en la lista de las obvias.
 */
import { z } from 'zod';
import { zEmail, zText } from '../validation/primitives';

export const USER_ROLES = ['admin', 'owner', 'manager', 'tenant'] as const;
export type Role = (typeof USER_ROLES)[number];

/** Etiquetas y descripción de cada rol. La tabla del README se genera de aquí. */
export const ROLE_LABELS: Record<Role, string> = {
  admin: 'Administrador',
  owner: 'Propietario',
  manager: 'Gestor',
  tenant: 'Inquilino',
};

/**
 * Las dos puertas de la pantalla de acceso.
 *
 * No es un rol más: es POR DÓNDE entra alguien. Un propietario y un inquilino ven aplicaciones
 * distintas —panel contra portal—, así que la pantalla pregunta primero por la puerta y
 * adapta el texto, el marcador de posición del correo y el destino.
 *
 * La puerta viaja al servidor y el servidor la COMPRUEBA. Sin eso sería decoración: un
 * inquilino que eligiese «Soy propietario» entraría igual y aterrizaría en un portal que no
 * había pedido. Y al revés, un propietario que se equivoque de puerta merece un mensaje que
 * lo diga, no un 403 genérico tres pantallas más adelante.
 */
export const LOGIN_AUDIENCES = ['owner', 'tenant'] as const;
export type LoginAudience = (typeof LOGIN_AUDIENCES)[number];

/** Qué roles entran por cada puerta. `admin`, `owner` y `manager` comparten la de gestión. */
export const AUDIENCE_ROLES: Record<LoginAudience, readonly Role[]> = {
  owner: ['admin', 'owner', 'manager'],
  tenant: ['tenant'],
};

export const AUDIENCE_LABELS: Record<LoginAudience, string> = {
  owner: 'Soy propietario',
  tenant: 'Soy inquilino',
};

/** La puerta que le corresponde a un rol. Es total: todo rol tiene exactamente una. */
export function audienceForRole(role: Role): LoginAudience {
  return role === 'tenant' ? 'tenant' : 'owner';
}

export function audienceAllows(audience: LoginAudience, role: Role): boolean {
  return AUDIENCE_ROLES[audience].includes(role);
}

/**
 * Las 20 contraseñas más usadas en España más las obvias del propio proyecto. No pretende ser
 * una lista completa (eso es un servicio de brechas), solo cortar lo indefendible.
 */
const OBVIOUS_PASSWORDS = new Set([
  '123456789012',
  'contraseña123',
  'password1234',
  'qwertyuiop12',
  'administrador',
  'naarrealstate',
  '111111111111',
  'abcdefghijkl',
]);

export const zPassword = z
  .string()
  .min(12, 'La contraseña debe tener al menos 12 caracteres.')
  .max(200, 'Máximo 200 caracteres.')
  .refine(
    (value) => !OBVIOUS_PASSWORDS.has(value.toLowerCase()),
    'Esa contraseña es demasiado común. Elige otra.',
  );

export const loginSchema = z.object({
  email: zEmail,
  /** En el login NO se aplica la política: solo se comprueba contra el hash almacenado. */
  password: z.string().min(1, 'Introduce tu contraseña.').max(200),
  /**
   * Opcional a propósito: un cliente antiguo —o `curl`— sigue entrando sin declararla. Cuando
   * viene, el servidor exige que la cuenta pertenezca a esa puerta.
   */
  audience: z.enum(LOGIN_AUDIENCES).optional(),
});

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Introduce tu contraseña actual.'),
    newPassword: zPassword,
  })
  .refine((value) => value.currentPassword !== value.newPassword, {
    path: ['newPassword'],
    message: 'La contraseña nueva debe ser distinta de la actual.',
  });

/** Alta de la cuenta del portal a partir del token de invitación. */
export const acceptInviteSchema = z.object({
  token: z.string().min(20, 'Enlace de invitación inválido.'),
  password: zPassword,
  fullName: zText(160, 'El nombre').optional(),
});

export type LoginInput = z.input<typeof loginSchema>;
export type ChangePasswordInput = z.input<typeof changePasswordSchema>;
export type AcceptInviteInput = z.input<typeof acceptInviteSchema>;

/**
 * Permisos declarativos. La API los aplica con un hook global *fail-closed* (una ruta que
 * olvide declararse queda protegida) y la UI los consulta para no ofrecer botones que el
 * servidor va a rechazar — que la API lo rechace es obligatorio; que la UI no lo ofrezca es
 * lo que hace que no parezca rota.
 */
export const PERMISSIONS = {
  'property:create': ['admin', 'owner', 'manager'],
  'property:update': ['admin', 'owner', 'manager'],
  'property:delete': ['admin', 'owner'],
  'portfolio:manage': ['admin', 'owner'],
  'unit:manage': ['admin', 'owner', 'manager'],
  'photo:manage': ['admin', 'owner', 'manager'],
  'valuation:manage': ['admin', 'owner', 'manager'],
  /** Financiación del inmueble: préstamos, seguros y gastos fijos. */
  'finance:manage': ['admin', 'owner'],
  /**
   * Ingresos y gastos: subir tickets y el extracto, resolver movimientos y conciliar el mes.
   * Separado de `finance:manage` porque es el trabajo DIARIO del gestor, y darle esto no tiene
   * por qué darle también la hipoteca y los seguros del inmueble.
   */
  'ledger:manage': ['admin', 'owner', 'manager'],
  'tenant:create': ['admin', 'owner', 'manager'],
  'tenant:update': ['admin', 'owner', 'manager'],
  'tenant:delete': ['admin', 'owner'],
  'tenant:export': ['admin', 'owner'],
  'lease:create': ['admin', 'owner', 'manager'],
  'lease:update': ['admin', 'owner', 'manager'],
  'lease:activate': ['admin', 'owner', 'manager'],
  'lease:renew': ['admin', 'owner', 'manager'],
  'lease:terminate': ['admin', 'owner'],
  'document:manage': ['admin', 'owner', 'manager'],
  'portal:invite': ['admin', 'owner', 'manager'],
  'user:manage': ['admin'],
  /** El Asistente: chat que consulta los datos de la organización (solo lectura). */
  'assistant:use': ['admin', 'owner', 'manager'],
} as const satisfies Record<string, readonly Role[]>;

export type Permission = keyof typeof PERMISSIONS;

export function can(role: Role | null | undefined, permission: Permission): boolean {
  if (!role) return false;
  return (PERMISSIONS[permission] as readonly Role[]).includes(role);
}
